import { fetchAuthSession } from 'aws-amplify/auth';
import { jobsService } from './jobsService';

type ToolExecuteResponse<T = any> = {
  ok: boolean;
  tool?: string;
  version?: string;
  result?: T;
  error?: any;
  job_id?: string;
};

function toErrorMessage(err: any): string {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (typeof err?.message === 'string' && err.message.trim()) return err.message;
  try {
    const s = JSON.stringify(err);
    if (s && s !== '{}' && s !== 'null') return s;
  } catch {}
  return String(err);
}

const getBaseUrl = () => {
  return process.env.NODE_ENV === 'development'
    ? '/Prod'
    : (process.env.REACT_APP_API_BASE_URL || 'https://p811zb8knk.execute-api.us-east-1.amazonaws.com/Prod');
};

export const toolClient = {
  execute: async <T = any>(
    tool: string,
    toolVersion: string,
    args: Record<string, any>,
    context?: Record<string, any>
  ): Promise<ToolExecuteResponse<T>> => {
    const baseUrl = getBaseUrl();
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) return { ok: false, error: { message: 'Not authenticated' } } as ToolExecuteResponse<T>;
    const url = `${baseUrl}/secure/tally-tools/execute`;
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ tool, tool_version: toolVersion, args, context: context || {} })
    });
    // Prefer JSON, but fall back to text for better diagnostics (API Gateway sometimes returns non-JSON errors)
    const rawText = await resp.text().catch(() => '');
    let data: any = {};
    if (rawText) {
      try { data = JSON.parse(rawText); } catch { data = {}; }
    }
    if (!resp.ok || data?.ok === false) {
      const err = data?.error || data || {};
      // Always surface something meaningful to callers
      const message =
        err?.message ||
        (typeof data?.message === 'string' ? data.message : null) ||
        `Tool execution failed (${resp.status})`;
      return {
        ok: false,
        error: {
          ...err,
          message,
          status: resp.status,
          url,
          raw: rawText ? rawText.slice(0, 2000) : undefined
        }
      } as ToolExecuteResponse<T>;
    }
    return (data as ToolExecuteResponse<T>) || ({ ok: true } as ToolExecuteResponse<T>);
  }
  ,

  /**
   * Submit a tool execution as an async job (safe for long LLM calls).
   * Returns a job_id that callers can poll via jobsService.get().
   */
  executeAsyncJob: async (
    tool: string,
    toolVersion: string,
    args: Record<string, any>,
    context?: Record<string, any>
  ): Promise<{ ok: boolean; job_id?: string; error?: any }> => {
    try {
      const submit = await jobsService.submitTallyTool(tool, toolVersion, args, context);
      return { ok: true, job_id: submit.job_id };
    } catch (e: any) {
      return { ok: false, error: { message: e?.message || 'Failed to submit tool job' } };
    }
  },

  /**
   * Submit a tool as an async job AND wait (poll) for completion.
   * This keeps UI call sites simple while still going through the async pipeline.
   */
  executeAsyncAndWait: async <T = any>(
    tool: string,
    toolVersion: string,
    args: Record<string, any>,
    context?: Record<string, any>,
    options?: { intervalMs?: number; timeoutMs?: number; onUpdate?: (job: any) => void }
  ): Promise<ToolExecuteResponse<T>> => {
    try {
      const submit = await jobsService.submitTallyTool(tool, toolVersion, args, context);
      const finalJob = await jobsService.waitForCompletion(submit.job_id, options);
      if (finalJob.status !== 'succeeded') {
        return {
          ok: false,
          job_id: submit.job_id,
          error: { message: toErrorMessage(finalJob.error) || `Job ended with status: ${finalJob.status}`, job: finalJob }
        } as ToolExecuteResponse<T>;
      }

      const inline = finalJob.result_inline;
      if (!inline || inline.ok !== true) {
        return {
          ok: false,
          job_id: submit.job_id,
          error: inline?.error ? { message: toErrorMessage(inline.error), raw: inline.error } : { message: 'Job succeeded but tool result_inline is missing/invalid', job: finalJob }
        } as ToolExecuteResponse<T>;
      }

      return {
        ok: true,
        job_id: submit.job_id,
        tool: inline.tool || tool,
        version: inline.version || toolVersion,
        result: inline.result as T
      } as ToolExecuteResponse<T>;
    } catch (e: any) {
      return { ok: false, error: { message: e?.message || 'Async tool execution failed' } } as ToolExecuteResponse<T>;
    }
  },
};


